import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { Loader } from '@mantine/core';
import { Check, Trash, X } from 'tabler-icons-react';

import { OrderAdditionalInfo } from 'components/OrderDetails';
import OrderDocuments from 'components/OrderDetails/OrderDocuments/OrderDocuments';
import {
   FinishEditingSectionButtonTooltip,
   FreeOrderBanner,
   FreeRelatedOrdersBanner,
   RetentionDateExpiredBanner,
} from 'components/Orders';
import { ActionsDropdown, CustomSwitch, StyledBadge, StyledButton } from 'components/shared';
import {
   CancelOrderModal,
   ClientSection,
   RemoveOrderModal,
   SelectedDeliveryTypeSection,
} from 'components/SupplierPanel';
import { useBannersDetails } from 'constants/banners';
import { useErrorDescriptions } from 'constants/errorDescriptions';
import { useOrderStatuses } from 'constants/orderStatuses';
import { PAGINATION_MAX_SIZE } from 'constants/pagination';
import { usePalletsTypes } from 'constants/palletTypes';
import { SUPPLIER_ORDERS_ALL_STATUSES_TAB } from 'constants/routes';
import WithAuth from 'hoc/withAuth';
import { useOrderDetails } from 'hooks/useOrderDetails';
import {
   BannerData,
   ClientUserDTO,
   CommercialNetworkDetailsDTO,
   OrderDetails,
   OrderDetailsDTO,
   OrderDetailsPageSection,
   OrderStatus,
   PalletDataWithPrice,
   PalletReportDTO,
   StorageDTO,
   SupplierEditAddedToNextOrderData,
   SupplierEditRegularOrderData,
   SupplierOrderDetailsPageModalsOrDialogs,
} from 'interfaces';
import { HTTPService } from 'service';
import { ChangeOrderStatusAction } from 'service/http/requests';
import { Banner, Divider, Tab } from 'storybook';
import { errorHandler } from 'utils/errorHandler';
import {
   calculatePalletsAmountAsLogisticMinimum,
   countChargesTotalAmount,
   countPalletsOrderTotalPrice,
   getFullAddress,
   getOrderDetailsData,
   getVisibleCharges,
   parseDate,
   scrollToElement,
} from 'utils/functions';
import { currencyFormatter } from 'utils/numberFormatter';

import AddedToNextOrderDetailsPage from './AddedToNextOrderDetailsPage/AddedToNextOrderDetailsPage';
import RegularOrderDetailsPage from './RegularOrderDetailsPage/RegularOrderDetailsPage';
import styles from './OrderDetailsPage.module.css';

const SECTIONS = {
   DELIVERY: 'deliverySection',
   RENTAL_PERIOD: 'rentalPeriodSection',
   PALLETS_CONFIGURATION: 'palletsConfigurationSection',
   STORAGE: 'storageSection',
};

const sectionsEditModeReducer = (
   state: {
      deliverySection: boolean;
      rentalPeriodSection: boolean;
      palletsConfigurationSection: boolean;
      storageSection: boolean;
   },
   action: { section: OrderDetailsPageSection; editMode: boolean },
) => {
   const { section, editMode } = action;
   return { ...state, [SECTIONS[section]]: editMode };
};

const sectionsLoadingReducer = (
   state: {
      deliverySection: boolean;
      rentalPeriodSection: boolean;
      palletsConfigurationSection: boolean;
      storageSection: boolean;
   },
   action: { section: OrderDetailsPageSection; isLoading: boolean },
) => {
   const { section, isLoading } = action;
   return { ...state, [SECTIONS[section]]: isLoading };
};

const REDUCER_INITIAL_STATE = {
   deliverySection: false,
   rentalPeriodSection: false,
   palletsConfigurationSection: false,
   storageSection: false,
};

const orderWithInvoicingStatuses: OrderStatus[] = ['IN_PROGRESS', 'DELIVERED', 'DURING_BILLING'];

const OrderDetailsPage = () => {
   const { t } = useTranslation();
   const params = useParams<{ id: string }>();
   const navigate = useNavigate();
   const [order, setOrder] = useState<OrderDetails>();
   const [client, setClient] = useState<ClientUserDTO>();
   const [storages, setStorages] = useState<StorageDTO[]>();
   const [commercialNetworks, setCommercialNetworks] = useState<CommercialNetworkDetailsDTO[]>();
   const [lostDamagedPalletsReports, setLostDamagedPalletsReports] = useState<PalletReportDTO[]>();
   const [isDataLoading, setIsDataLoading] = useState(false);
   const [isSendAndCreateInvoiceLoading, setIsSendAndCreateInvoiceLoading] = useState(false);
   const [activeTab, setActiveTab] = useState<'ORDER' | 'DOCUMENTS'>('ORDER');
   const [selectedModalOrDialog, setSelectedModalOrDialog] =
      useState<SupplierOrderDetailsPageModalsOrDialogs>(null);
   const [bannerData, setBannerData] = useState<BannerData | null>(null);
   const [dismissedSaveStatusBannerKey, setDismissedSaveStatusBannerKey] = useState<string | null>(
      null,
   );
   const [sectionsEditMode, dispatchSectionsEditMode] = useReducer(
      sectionsEditModeReducer,
      REDUCER_INITIAL_STATE,
   );
   const [sectionsLoading, dispatchSectionsLoading] = useReducer(
      sectionsLoadingReducer,
      REDUCER_INITIAL_STATE,
   );
   const orderDetailsHeaderRef = useRef<null | HTMLDivElement>(null);
   const saveStatusBannerRef = useRef<null | HTMLDivElement>(null);
   const orderDocumentsContentRef = useRef<{ fetchDocuments: () => void }>(null);
   const { pallets, palletsSum, delayTimeInDaysFromRetentionDate, isRetentionDateExpired } =
      useOrderDetails({
         order,
      });

   const { ORDER_STATUSES_BADGE_DATA } = useOrderStatuses();
   const { SUPPLIER_ORDER_DETAILS_BANNERS } = useBannersDetails();

   const { CREATE_AND_SEND_INVOICE_ERROR_DESCRIPTION, DEFAULT_ERROR_DESCRIPTIONS } =
      useErrorDescriptions();

   const { palletTypesData } = usePalletsTypes();
   const orderId = order?.id;
   const orderSaveStatus = order?.saveStatus;

   const isOrderNonstandard = order?.personalPickUp || !order?.hasMetLogisticsMinimum;

   const scrollToTop = useCallback(() => scrollToElement(orderDetailsHeaderRef), []);

   const saveStatusBannerData = useMemo(() => {
      if (!order || order.saveStatus === 'IDLE') {
         return null;
      }
      if (order.saveStatus === 'IN_PROGRESS') {
         return {
            variant: 'info',
            title: 'Zapis w toku',
            description: 'Zapis jest realizowany w tle. Możesz wrócić do zamówienia później.',
         } as BannerData;
      }
      if (order.saveStatus === 'SUCCESS') {
         return {
            variant: 'success',
            description: 'Zapis zakończony sukcesem.',
         } as BannerData;
      }
      return {
         variant: 'error',
         description: 'Wystąpił błąd podczas zapisu.',
      } as BannerData;
   }, [order]);

   const saveStatusBannerKey = useMemo(
      () =>
         saveStatusBannerData
            ? `${saveStatusBannerData.variant}:${saveStatusBannerData.title ?? ''}:${
                 saveStatusBannerData.description
              }`
            : null,
      [saveStatusBannerData],
   );

   const isSaveStatusBannerVisible =
      Boolean(saveStatusBannerData) && saveStatusBannerKey !== dismissedSaveStatusBannerKey;

   useEffect(() => {
      if (!isSaveStatusBannerVisible || !saveStatusBannerRef.current) {
         return;
      }
      scrollToElement(saveStatusBannerRef);
   }, [isSaveStatusBannerVisible, saveStatusBannerKey]);

   useEffect(() => {
      if (saveStatusBannerKey !== dismissedSaveStatusBannerKey) {
         return;
      }
      if (!saveStatusBannerKey) {
         setDismissedSaveStatusBannerKey(null);
      }
   }, [dismissedSaveStatusBannerKey, saveStatusBannerKey]);

   const handleError = useCallback(
      (error: unknown, errorDescription: string) => {
         scrollToTop();
         errorHandler(error, () =>
            setBannerData({
               variant: 'error',
               description: errorDescription,
            }),
         );
      },
      [scrollToTop],
   );

   useEffect(() => {
      const fetchData = async () => {
         setIsDataLoading(true);
         if (params.id) {
            try {
               const getOrderDetailsResponse = await HTTPService.getOrderDetails(params.id);
               const orderDetailsData = getOrderDetailsData(getOrderDetailsResponse.data);
               setOrder(orderDetailsData);
               if (orderDetailsData.mode === 'REGULAR_ORDER') {
                  const clientId = orderDetailsData.createdBy.id;
                  const [
                     getClientDetailsResponse,
                     getStoragesResponse,
                     getReceivedLostOrDamagedPalletsResponse,
                     getCommercialNetworksResponse,
                  ] = await Promise.all([
                     HTTPService.getUserDetails(clientId),
                     HTTPService.getStorageList({ size: PAGINATION_MAX_SIZE }),
                     HTTPService.getReceivedLostOrDamagedPallets(params.id, undefined, {
                        size: PAGINATION_MAX_SIZE,
                     }),
                     HTTPService.getCommercialNetworks(
                        { userId: clientId },
                        { page: 0, size: PAGINATION_MAX_SIZE },
                     ),
                  ]);
                  setClient(getClientDetailsResponse.data);
                  setStorages(getStoragesResponse.data.content);
                  setLostDamagedPalletsReports(
                     getReceivedLostOrDamagedPalletsResponse.data.content,
                  );
                  setCommercialNetworks(getCommercialNetworksResponse.data.content);
               } else {
                  const getClientDetailsResponse = await HTTPService.getUserDetails(
                     orderDetailsData.createdBy.id,
                  );
                  setClient(getClientDetailsResponse.data);
               }
            } catch (error) {
               handleError(error, DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA);
            } finally {
               setIsDataLoading(false);
            }
         }
      };
      fetchData();
   }, [params.id, handleError, DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA]);

   useEffect(() => {
      if (!orderId || orderSaveStatus !== 'IN_PROGRESS') {
         return;
      }
      let isFinished = false;
      const pollSaveStatus = async () => {
         try {
            const { data: saveStatus } = await HTTPService.getOrderSaveStatus(orderId);
            if (isFinished) {
               return;
            }
            setOrder(prevOrder => {
               if (!prevOrder) {
                  return prevOrder;
               }
               if (
                  prevOrder.saveStatus !== saveStatus.status ||
                  prevOrder.saveErrorMessage !== saveStatus.errorMessage
               ) {
                  return {
                     ...prevOrder,
                     saveStatus: saveStatus.status,
                     saveErrorMessage: saveStatus.errorMessage ?? null,
                  };
               }
               return prevOrder;
            });
         } catch (error) {
            return;
         }
      };
      const interval = window.setInterval(pollSaveStatus, 10000);
      pollSaveStatus();
      return () => {
         isFinished = true;
         window.clearInterval(interval);
      };
   }, [orderId, orderSaveStatus]);

   useEffect(() => {
      if (!orderId || orderSaveStatus !== 'SUCCESS') {
         return;
      }
      let isFinished = false;

      HTTPService.getOrderDetails(orderId)
         .then(({ data }) => {
            if (isFinished) {
               return;
            }
            const orderDetailsData = getOrderDetailsData(data);
            setOrder(prevOrder => ({
               ...orderDetailsData,
               saveStatus: prevOrder?.saveStatus ?? 'SUCCESS',
               saveErrorMessage: prevOrder?.saveErrorMessage ?? null,
            }));
         })
         .catch(() => {
            return;
         });

      return () => {
         isFinished = true;
      };
   }, [orderId, orderSaveStatus]);

   const fetchOrder = useCallback(() => {
      if (orderId) {
         setIsDataLoading(true);
         HTTPService.getOrderDetails(orderId)
            .then(res => {
               const orderDetailsData = getOrderDetailsData(res.data);
               setOrder(orderDetailsData);
            })
            .catch(error => handleError(error, DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA))
            .finally(() => setIsDataLoading(false));
      }
   }, [orderId, handleError, DEFAULT_ERROR_DESCRIPTIONS.FETCH_DATA]);

   const handleCreateAndSendInvoice = () => {
      setIsSendAndCreateInvoiceLoading(true);
      HTTPService.createAndSendInvoice(order!.id)
         .then(() => {
            setOrder(prevOrder => prevOrder && { ...prevOrder, hasInvoice: true });
            setActiveTab('ORDER');
            setSelectedModalOrDialog('SUCCESS_CREATE_AND_REPORT_INVOICE_DIALOG');
         })
         .catch(error => handleError(error, CREATE_AND_SEND_INVOICE_ERROR_DESCRIPTION))
         .finally(() => setIsSendAndCreateInvoiceLoading(false));
   };

   const handleOpenCancelOrderModal = () => setSelectedModalOrDialog('CANCEL_ORDER_MODAL');

   const handleOpenRemoveOrderModal = () => setSelectedModalOrDialog('REMOVE_ORDER_MODAL');

   const handleCloseModalOrDialog = useCallback(() => setSelectedModalOrDialog(null), []);

   const handleCloseBanner = () => setBannerData(null);

   const handleCloseSaveStatusBanner = () => {
      if (saveStatusBannerKey) {
         setDismissedSaveStatusBannerKey(saveStatusBannerKey);
      }
   };

   const handleSuccessOrderAction = (
      action: ChangeOrderStatusAction,
      updatedOrder: OrderDetailsDTO,
   ) => {
      setBannerData(SUPPLIER_ORDER_DETAILS_BANNERS[action]);
      setOrder(getOrderDetailsData(updatedOrder));
      scrollToElement(orderDetailsHeaderRef);
   };

   const handleSuccessCancelOrder = (updatedOrder: OrderDetailsDTO) => {
      handleSuccessOrderAction('CANCEL', updatedOrder);
      if (activeTab === 'ORDER') {
         return;
      }
      orderDocumentsContentRef.current?.fetchDocuments();
   };

   const handleSuccessRemoveOrder = () =>
      navigate(SUPPLIER_ORDERS_ALL_STATUSES_TAB, {
         replace: true,
         state: { successRemoveOrder: true },
      });

   const handleClickAcceptOrderBtn = useCallback(() => {
      if (!order) {
         return;
      }
      if (!order.storage) {
         setSelectedModalOrDialog('NO_PICKUP_ADDRESS_MODAL');
         return;
      }
      setSelectedModalOrDialog('ACCEPT_ORDER_MODAL');
   }, [order]);

   const handleOpenDeliverOrderModal = useCallback(() => {
      if (!order) {
         return;
      }

      if (order.palletDeliveryDate) {
         const deliveryDate = parseDate(order.palletDeliveryDate);
         const today = new Date();
         deliveryDate.setHours(0, 0, 0, 0);
         today.setHours(0, 0, 0, 0);

         if (deliveryDate > today) {
            setBannerData({
               variant: 'error',
               description: t('order_details_page.delivery_date_in_future_error'),
            });
            scrollToTop();
            return;
         }
      }

      setSelectedModalOrDialog('DELIVER_ORDER_MODAL');
   }, [order, scrollToTop, t]);

   const renderContent = () => {
      if (order && client && orderFormValues) {
         if (
            order.mode === 'REGULAR_ORDER' &&
            orderFormValues.orderType === 'REGULAR_ORDER' &&
            storages &&
            commercialNetworks &&
            lostDamagedPalletsReports
         ) {
            return (
               <RegularOrderDetailsPage
                  order={order}
                  initialFormValues={orderFormValues}
                  client={client}
                  storages={storages}
                  commercialNetworks={commercialNetworks}
                  lostDamagedPalletsReports={lostDamagedPalletsReports}
                  pallets={pallets}
                  palletsSum={palletsSum}
                  chargesTotalAmount={chargesTotalAmount}
                  isOrderFree={isFree}
                  isOrderNonstandard={isOrderNonstandard}
                  sectionsEditMode={sectionsEditMode}
                  selectedModalOrDialog={selectedModalOrDialog}
                  sectionsLoading={sectionsLoading}
                  fetchOrder={fetchOrder}
                  setOrder={setOrder}
                  setLostDamagedPalletsReports={setLostDamagedPalletsReports}
                  showModalOrDialog={setSelectedModalOrDialog}
                  handleCloseModalOrDialog={handleCloseModalOrDialog}
                  handleSuccessOrderAction={handleSuccessOrderAction}
                  dispatchSectionsEditMode={dispatchSectionsEditMode}
                  dispatchSectionsLoading={dispatchSectionsLoading}
                  scrollToTop={scrollToTop}
                  showBanner={setBannerData}
                  onError={handleError}
               />
            );
         } else if (
            order.mode === 'ADDED_TO_NEXT_ORDER' &&
            orderFormValues.orderType === 'ADDED_TO_NEXT_ORDER'
         ) {
            return (
               <AddedToNextOrderDetailsPage
                  order={order}
                  initialFormValues={orderFormValues}
                  client={client}
                  pallets={pallets}
                  palletsSum={palletsSum}
                  isPalletsConfigurationSectionEditMode={
                     sectionsEditMode.palletsConfigurationSection
                  }
                  isPalletsConfigurationSectionLoading={sectionsLoading.palletsConfigurationSection}
                  setOrder={setOrder}
                  dispatchSectionsEditMode={dispatchSectionsEditMode}
                  dispatchSectionsLoading={dispatchSectionsLoading}
                  onError={handleError}
               />
            );
         }
      }
   };

   const orderFormValues = useMemo<
      SupplierEditRegularOrderData | SupplierEditAddedToNextOrderData | undefined
   >(() => {
      if (order) {
         const palletsData: PalletDataWithPrice[] = order.pallets.map(pallet => ({
            name: pallet.palletType,
            amount: pallet.shownAsMultipleOfTheMinimum
               ? calculatePalletsAmountAsLogisticMinimum(
                    pallet.palletType,
                    pallet.amount,
                    palletTypesData,
                 ).toString()
               : pallet.amount.toString(),
            orderType: pallet.shownAsMultipleOfTheMinimum ? 'LOGISTIC_MINIMUM' : 'CUSTOM',
            pricePerUnit: pallet.pricePerUnit,
         }));
         if (order.mode === 'REGULAR_ORDER') {
            const {
               personalPickUp,
               address: deliveryAddress,
               storage,
               palletDeliveryDate: formattedPalletDeliveryDate,
            } = order;
            const address =
               !personalPickUp && deliveryAddress
                  ? {
                       label: getFullAddress(
                          deliveryAddress.street,
                          deliveryAddress.zipCode,
                          deliveryAddress.city,
                          deliveryAddress.name,
                       ),
                       value: {
                          name: deliveryAddress.name,
                          street: deliveryAddress.street,
                          city: deliveryAddress.city,
                          zipCode: deliveryAddress.zipCode,
                       },
                    }
                  : 'SELF_PICKUP';
            const orderFormData: SupplierEditRegularOrderData = {
               orderType: 'REGULAR_ORDER',
               address,
               palletDeliveryDate: parseDate(formattedPalletDeliveryDate),
               pallets: palletsData,
               storage: storage
                  ? {
                       value: { storageId: storage.id, storageType: storage.type },
                       label: `${storage.address.street}, ${storage.address.zipCode} ${storage.address.city}`,
                    }
                  : null,
               palletCustomPrices: {
                  edhpPalletPrice: 0,
                  euroPalletPrice: 0,
                  halfPalletNDHPPrice: 0,
                  quarterPalletCDHPPrice: 0,
               },
            };
            return orderFormData;
         } else if (order.mode === 'ADDED_TO_NEXT_ORDER') {
            const orderFormData: SupplierEditAddedToNextOrderData = {
               orderType: 'ADDED_TO_NEXT_ORDER',
               pallets: palletsData,
            };
            return orderFormData;
         }
      }
   }, [order, palletTypesData]);

   const chargesTotalAmount = useMemo(
      () => (order ? countChargesTotalAmount(order.additionalCharges) : 0),
      [order],
   );

   const visibleChargesCount = useMemo(
      () => (order ? getVisibleCharges(order.additionalCharges).length : 0),
      [order],
   );

   const orderDetailsOptions = useMemo(() => {
      if (order?.status === 'ENDED') {
         return;
      }
      if (order?.status !== 'CANCELED') {
         return [
            {
               icon: <X />,
               text: t('order_details_page.cancel_order'),
               onClick: handleOpenCancelOrderModal,
            },
         ];
      }
      if (order?.status === 'CANCELED') {
         return [
            {
               icon: <Trash />,
               text: t('order_details_page.remove_order'),
               onClick: handleOpenRemoveOrderModal,
               dangerOption: true,
            },
         ];
      }
   }, [order?.status, t]);

   const isSomeSectionInEditMode =
      sectionsEditMode.deliverySection ||
      sectionsEditMode.palletsConfigurationSection ||
      sectionsEditMode.rentalPeriodSection ||
      sectionsEditMode.storageSection;

   const actionButtons = useMemo(() => {
      switch (order?.status) {
         case 'WAITING':
            return (
               <>
                  <FinishEditingSectionButtonTooltip isVisible={isSomeSectionInEditMode}>
                     <StyledButton
                        icon={<X size={20} />}
                        text={
                           isOrderNonstandard
                              ? t('order_details_page.reject_non_standard_order')
                              : t('order_details_page.reject_order')
                        }
                        variant="filled-danger"
                        onClick={() => setSelectedModalOrDialog('REJECT_ORDER_MODAL')}
                        disabled={isSomeSectionInEditMode}
                     />
                  </FinishEditingSectionButtonTooltip>
                  <FinishEditingSectionButtonTooltip isVisible={isSomeSectionInEditMode}>
                     <StyledButton
                        icon={<Check size={20} />}
                        text={
                           isOrderNonstandard
                              ? t('order_details_page.accept_non_standard_order')
                              : t('order_details_page.accept_order')
                        }
                        variant="filled-primary"
                        onClick={handleClickAcceptOrderBtn}
                        disabled={isSomeSectionInEditMode}
                     />
                  </FinishEditingSectionButtonTooltip>
               </>
            );
         case 'DELIVERED':
            return (
               <FinishEditingSectionButtonTooltip isVisible={isSomeSectionInEditMode}>
                  <StyledButton
                     variant="filled-primary"
                     text={t('sendToSettlement.title')}
                     onClick={() => setSelectedModalOrDialog('SETTLEMENT_MODAL')}
                     disabled={isSomeSectionInEditMode}
                  />
               </FinishEditingSectionButtonTooltip>
            );
         case 'IN_PROGRESS':
            return (
               <FinishEditingSectionButtonTooltip isVisible={isSomeSectionInEditMode}>
                  <StyledButton
                     variant="filled-primary"
                     text={t('order_details_page.mark_as_delivered')}
                     onClick={handleOpenDeliverOrderModal}
                     disabled={isSomeSectionInEditMode}
                  />
               </FinishEditingSectionButtonTooltip>
            );
         case 'DURING_BILLING':
            return (
               <StyledButton
                  variant="filled-primary"
                  text={
                     !!visibleChargesCount
                        ? t('order_details_page.send_to_payment_with_charges')
                        : t('order_details_page.send_to_payment')
                  }
                  onClick={() => setSelectedModalOrDialog('SEND_FOR_PAYMENT')}
               />
            );
         case 'TO_PAY':
            return (
               <StyledButton
                  icon={<Check size={20} />}
                  variant="filled-primary"
                  text={t('order_details_page.close_order')}
                  onClick={() => setSelectedModalOrDialog('CLOSE_ORDER_MODAL')}
               />
            );
         default:
            return null;
      }
   }, [
      order?.status,
      visibleChargesCount,
      handleClickAcceptOrderBtn,
      handleOpenDeliverOrderModal,
      isOrderNonstandard,
      isSomeSectionInEditMode,
      t,
   ]);

   const isFree = order?.deliveryMethod?.free;
   const orderTotalPrice = !isFree
      ? `${currencyFormatter.format(countPalletsOrderTotalPrice(pallets) + chargesTotalAmount)} PLN`
      : t('common.free');

   return (
      <>
         {isDataLoading && (
            <div className={styles.loaderContainer}>
               <Loader color="var(--primary-green)" />
            </div>
         )}
         {order && !isDataLoading && (
            <>
               <CancelOrderModal
                  opened={selectedModalOrDialog === 'CANCEL_ORDER_MODAL'}
                  onClose={handleCloseModalOrDialog}
                  selectedOrderId={order.id}
                  onSuccessCancelOrder={handleSuccessCancelOrder}
               />
               <RemoveOrderModal
                  opened={selectedModalOrDialog === 'REMOVE_ORDER_MODAL'}
                  onClose={handleCloseModalOrDialog}
                  selectedOrderId={order.id}
                  onSuccessRemoveOrder={handleSuccessRemoveOrder}
               />
               <div ref={orderDetailsHeaderRef} className={styles.detailsHeader}>
                  <div className={styles.headerLeft}>
                     <div className={styles.orderNumberContainer}>
                        <h2>{t('order_details_page.order_number', { id: params.id })}</h2>
                        <StyledBadge
                           style={{ marginRight: 24 }}
                           text={ORDER_STATUSES_BADGE_DATA[order.status].label}
                           variant={ORDER_STATUSES_BADGE_DATA[order.status].variant}
                        />
                        {orderWithInvoicingStatuses.includes(order.status) &&
                           !order.hasInvoice &&
                           !isFree && (
                              <StyledButton
                                 type="button"
                                 text={t('order_details_page.create_invoice')}
                                 variant="outlined-primary"
                                 loading={isSendAndCreateInvoiceLoading}
                                 onClick={handleCreateAndSendInvoice}
                              />
                           )}
                     </div>
                     <OrderAdditionalInfo
                        createdDate={order.createdDate}
                        internalOrderId={order.internalOrderId}
                     />
                  </div>
                  {orderDetailsOptions && <ActionsDropdown large options={orderDetailsOptions!} />}
               </div>
               {order.deliveryMethod?.free && (
                  <FreeOrderBanner
                     orderStatus={order.status}
                     isOrderAddedToNextOrder={order.deliveryMethod.addedToNextOrder}
                     orderAddedToOrderId={order.addedToOrderId}
                     className={styles.banner}
                  />
               )}
               {order.includesReceivedDamagedOrLostPallets && (
                  <Banner
                     variant="info"
                     children={t('orderDetails.damagedPalletsReported')}
                     fullWidth
                     className={styles.banner}
                  />
               )}
               {!!order.relatedAdditionalOrderIDs.length && (
                  <FreeRelatedOrdersBanner
                     freeRelatedOrderIds={order.relatedAdditionalOrderIDs}
                     className={styles.banner}
                  />
               )}
               {order.status === 'WAITING' && isOrderNonstandard && (
                  <Banner
                     variant="warning"
                     children={
                        !order.hasMetLogisticsMinimum
                           ? t('order_details_page.non_standard_order_no_logistic_minimum_banner')
                           : t('order_details_page.non_standard_order_banner')
                     }
                     className={styles.banner}
                     fullWidth
                  />
               )}
               {isRetentionDateExpired && (
                  <RetentionDateExpiredBanner
                     role="ROLE_SYSTEM_ADMIN"
                     delayTimeInDaysFromRetentionDate={delayTimeInDaysFromRetentionDate}
                  />
               )}
               {isSaveStatusBannerVisible && saveStatusBannerData && (
                  <div ref={saveStatusBannerRef}>
                     <Banner
                        variant={saveStatusBannerData.variant}
                        title={saveStatusBannerData.title}
                        children={saveStatusBannerData.description}
                        onClose={handleCloseSaveStatusBanner}
                        withCloseIcon
                        fullWidth
                        className={styles.banner}
                     />
                  </div>
               )}
               {bannerData && (
                  <Banner
                     variant={bannerData.variant}
                     title={bannerData.title}
                     children={bannerData.description}
                     onClose={handleCloseBanner}
                     withCloseIcon
                     fullWidth
                     className={styles.banner}
                  />
               )}
               <div className={styles.tabsSection}>
                  <div className={styles.tabsContainer}>
                     <Tab
                        active={activeTab === 'ORDER'}
                        text={t('order_details.title')}
                        onClick={() => setActiveTab('ORDER')}
                     />
                     <Tab
                        active={activeTab === 'DOCUMENTS'}
                        text={t('orderDocuments.sectionTitle')}
                        onClick={() => setActiveTab('DOCUMENTS')}
                     />
                  </div>
                  <Divider className={styles.tabsBorder} variant="horizontal" />
               </div>
               {activeTab === 'ORDER' ? (
                  <>
                     {isFree && (
                        <>
                           <CustomSwitch
                              label={t('order_summary.free_order_switch')}
                              checked
                              disabled
                              offLabel={<X size={16} color="white" />}
                              onLabel={<Check size={16} color="white" />}
                              style={{ margin: '0 0 48px 24px' }}
                           />
                           <SelectedDeliveryTypeSection
                              selectedDeliveryType={
                                 order.deliveryMethod?.addedToNextOrder
                                    ? 'ADDED_TO_NEXT_ORDER'
                                    : 'SEPARATE_ORDER'
                              }
                           />
                        </>
                     )}
                     {client && <ClientSection {...client} />}
                     {renderContent()}
                     <div className={styles.summaryContainer}>
                        <p>
                           <span>{t('order_details_page.summary_title')}</span>
                           <span>{orderTotalPrice}</span>
                        </p>
                     </div>
                     {actionButtons && (
                        <div className={styles.buttonsContainer}>{actionButtons}</div>
                     )}
                  </>
               ) : (
                  <OrderDocuments
                     ref={orderDocumentsContentRef}
                     orderId={order.id}
                     displayBanner={setBannerData}
                     supplierMode
                  />
               )}
            </>
         )}
      </>
   );
};

export default WithAuth(OrderDetailsPage, 'ROLE_SYSTEM_ADMIN');
